import { useEffect, useState } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useDashboardStore } from '@/stores/dashboardStore';
import { useMapStore } from '@/stores/mapStore';
import { useDashboard } from '@/hooks/useDashboard';
import { useFleetPanelResize } from '@/hooks/useFleetPanelResize';
import { fetchAllFleet } from '@/api/ares';
import type { GeoJSONFeature } from '@/types/map';
import TopBar from './TopBar';
import MobileToggle from './MobileToggle';
import ChatSidebar from '@/components/chat/ChatSidebar';
import MapContainer from '@/components/map/MapContainer';
import RoutePanel from '@/components/route/RoutePanel';
import FleetGridPanel from '@/components/chat/FleetGridPanel';
import ChartModal from '@/components/charts/ChartModal';
import SettingsModal from '@/components/settings/SettingsModal';
import { ToastContainer } from '@/components/ui';
import AdminPanel from '@/components/admin/AdminPanel';
import LoadingOverlay from '@/components/ui/LoadingOverlay';
import { Zap, ChevronUp } from 'lucide-react';

export default function AppLayout() {
  const setIsMobile = useUIStore((s) => s.setIsMobile);
  const isInitialLoading = useUIStore((s) => s.isInitialLoading);
  const setInitialLoading = useUIStore((s) => s.setInitialLoading);
  const fleetPanelOpen = useUIStore((s) => s.fleetPanelOpen);
  const fleetData = useUIStore((s) => s.fleetData);
  const setFleetPanelOpen = useUIStore((s) => s.setFleetPanelOpen);

  const updateDashboardStats = useDashboardStore((s) => s.updateStats);
  const setFeatures = useMapStore((s) => s.setFeatures);
  const { fleetPanelHeight, onMouseDown: onResizerMouseDown, resizerRef } = useFleetPanelResize();
  const [isContextReady, setIsContextReady] = useState(false);

  const criticalCount = fleetData.filter((t: any) => t.theft_percent > 75).length;

  useEffect(() => {
    const initializeFreshSession = async () => {
      try {
        const data = await fetchAllFleet();
        if (data.success && data.map?.features?.length) {
          if (data.stats) {
            updateDashboardStats(data.stats);
          }
          setFeatures(data.map.features as GeoJSONFeature[]);
          console.log(`✅ Flota cargada: ${data.count} viajes activos`);
        } else {
          console.error('No se pudo cargar la flota:', data.error);
        }
      } catch (error) {
        console.error('Failed to initialize fresh session:', error);
      } finally {
        setIsContextReady(true);
      }
    };
    initializeFreshSession();
  }, [setFeatures, updateDashboardStats]);

  useDashboard();

  useEffect(() => {
    if (isInitialLoading && isContextReady) {
      const timer = setTimeout(() => setInitialLoading(false), 800);
      return () => clearTimeout(timer);
    }
  }, [isInitialLoading, isContextReady, setInitialLoading]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', check);
    check();
    return () => window.removeEventListener('resize', check);
  }, [setIsMobile]);

  // Forward center-map → leaflet-center (handled inside MapContainer's TileUpdater)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.lat != null && detail?.lon != null) {
        window.dispatchEvent(new CustomEvent('leaflet-center', { detail }));
      }
    };
    window.addEventListener('center-map', handler);
    return () => window.removeEventListener('center-map', handler);
  }, []);

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {isInitialLoading && <LoadingOverlay />}

      <TopBar />

      {/* ── MAIN ROW (below TopBar) ── */}
      <div className="flex flex-1 overflow-hidden mt-14">

        {/* LEFT: Chat sidebar — always full height, never affected by fleet panel */}
        <ChatSidebar />

        {/* RIGHT: Map + Fleet — this column splits vertically when fleet opens */}
        <div className="flex flex-col flex-1 overflow-hidden relative">

          {/* MAP AREA — flex-1 when fleet closed, dynamic height when open */}
          <div
            className={`flex overflow-hidden relative transition-all duration-300 ease-out
                        ${fleetPanelOpen ? 'flex-shrink-0' : 'flex-1'}`}
            style={fleetPanelOpen ? { height: `calc(100% - ${fleetPanelHeight}px)` } : {}}
          >
            <MapContainer />
            <RoutePanel />

            {/* Handle — sits on bottom edge of map, only when fleet is closed */}
            {fleetData.length > 0 && !fleetPanelOpen && (
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 z-[500] pointer-events-auto">
                <button
                  onClick={() => setFleetPanelOpen(true)}
                  className="flex items-center gap-3 px-5 py-2 rounded-t-2xl
                             glass-elite border border-b-0 border-border-strong
                             hover:bg-bg-elevated transition-all duration-200 group shadow-2xl"
                >
                  <Zap size={13} className="text-accent-purple animate-pulse flex-shrink-0" />
                  <span className="text-[11px] font-black uppercase tracking-widest text-text-primary whitespace-nowrap">
                    Centro de Flota
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-accent-purple/20 border border-accent-purple/40 text-[10px] font-black text-accent-purple">
                    {fleetData.length}
                  </span>
                  {criticalCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-accent-red/20 border border-accent-red/40 text-[10px] font-black text-accent-red animate-pulse">
                      {criticalCount} críticas
                    </span>
                  )}
                  <ChevronUp size={13} className="text-text-muted group-hover:text-text-primary transition-colors" />
                </button>
              </div>
            )}
          </div>

          {/* RESIZER BAR — only shown when fleet is open */}
          {fleetPanelOpen && (
            <div
              ref={resizerRef}
              onMouseDown={onResizerMouseDown}
              className="h-1.5 w-full cursor-row-resize bg-border-strong flex items-center justify-center hover:bg-accent-purple/40 transition-colors z-[600] group relative"
            >
              <div className="w-16 h-1 rounded-full bg-text-muted group-hover:bg-white group-active:bg-accent-purple transition-all" />
              {/* Glow effect on hover */}
              <div className="absolute inset-0 bg-accent-purple/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
            </div>
          )}

          {/* FLEET GRID — grows from h-0 to dynamic height when fleet is open */}
          <div
            className={`flex-shrink-0 overflow-hidden border-t border-border-strong transition-all duration-300 ease-out
                        ${!fleetPanelOpen ? 'h-0' : ''}`}
            style={fleetPanelOpen ? { height: `${fleetPanelHeight}px` } : {}}
          >
            <FleetGridPanel />
          </div>

        </div>
      </div>

      <ChartModal />
      <SettingsModal />
      <MobileToggle />
      <ToastContainer />
      <AdminPanel />
    </div>
  );
}
